/**
 * Types for the translation service that connects to the backend OpenAI translation endpoint
 */

/**
 * Supported translation languages
 */
export type TranslationLanguage = 'nl' | 'en' | 'de' | 'fr' | 'pt';

/**
 * Language configuration with display names
 */
export interface LanguageConfig {
  readonly code: TranslationLanguage;
  readonly name: string;
  readonly flag: string;
}

/**
 * Available languages for translation
 */
export const TRANSLATION_LANGUAGES: readonly LanguageConfig[] = [
  { code: 'nl', name: 'Nederlands', flag: 'nl' },
  { code: 'en', name: 'English', flag: 'gb' },
  { code: 'de', name: 'Deutsch', flag: 'de' },
  { code: 'fr', name: 'Fran\u00e7ais', flag: 'fr' },
  { code: 'pt', name: 'Portugu\u00eas', flag: 'pt' },
] as const;

/**
 * Request payload for a single translation
 */
export interface TranslationRequest {
  readonly text: string;
  readonly targetLanguage: TranslationLanguage;
  readonly sourceLanguage?: TranslationLanguage;
}

/**
 * Response from a single translation
 */
export interface TranslationResult {
  readonly translatedText: string;
  readonly targetLanguage: TranslationLanguage;
  readonly success: boolean;
  readonly error?: string;
}

/**
 * Aggregated results from multiple translations
 */
export interface TranslationResults {
  readonly results: TranslationResult[];
  readonly successCount: number;
  readonly failureCount: number;
}

/**
 * Status of a translation operation
 */
export type TranslationStatus = 'pending' | 'loading' | 'success' | 'error';

/**
 * Per-language translation progress tracking
 */
export interface TranslationProgress {
  readonly language: TranslationLanguage;
  readonly status: TranslationStatus;
  readonly error?: string;
}

/**
 * Options for translation modal/dialog
 */
export interface TranslationDialogOptions {
  readonly sourceText: string;
  readonly sourceLanguage: TranslationLanguage;
  readonly availableLanguages: TranslationLanguage[];
  readonly existingTranslations: Partial<Record<TranslationLanguage, string>>;
}

/**
 * Result from translation dialog
 */
export interface TranslationDialogResult {
  readonly confirmed: boolean;
  readonly selectedLanguages?: TranslationLanguage[];
  readonly overwriteExisting?: boolean;
}

/**
 * Field mapping for multi-language entity fields
 */
export interface MultiLanguageFieldConfig {
  readonly baseField: string;
  readonly languageFields: Record<TranslationLanguage, string>;
}

/**
 * Company profile translatable fields configuration
 */
export const COMPANY_PROFILE_FIELDS: readonly MultiLanguageFieldConfig[] = [
  {
    baseField: 'description',
    languageFields: {
      nl: 'description',
      en: 'descriptionEn',
      de: 'descriptionDe',
      fr: 'descriptionFr',
      pt: 'descriptionPt',
    },
  },
  {
    baseField: 'uniqueText',
    languageFields: {
      nl: 'uniqueText',
      en: 'uniqueTextEn',
      de: 'uniqueTextDe',
      fr: 'uniqueTextFr',
      pt: 'uniqueTextPt',
    },
  },
  {
    baseField: 'opportunitiesText',
    languageFields: {
      nl: 'opportunitiesText',
      en: 'opportunitiesTextEn',
      de: 'opportunitiesTextDe',
      fr: 'opportunitiesTextFr',
      pt: 'opportunitiesTextPt',
    },
  },
] as const;

/**
 * Job posting translatable fields configuration
 */
export const JOB_POSTING_FIELDS: readonly MultiLanguageFieldConfig[] = [
  {
    baseField: 'title',
    languageFields: {
      nl: 'title',
      en: 'titleEn',
      de: 'titleDe',
      fr: 'titleFr',
      pt: 'titlePt',
    },
  },
  {
    baseField: 'location',
    languageFields: {
      nl: 'location',
      en: 'locationEn',
      de: 'locationDe',
      fr: 'locationFr',
      pt: 'locationPt',
    },
  },
  {
    baseField: 'description',
    languageFields: {
      nl: 'description',
      en: 'descriptionEn',
      de: 'descriptionDe',
      fr: 'descriptionFr',
      pt: 'descriptionPt',
    },
  },
  {
    baseField: 'requirements',
    languageFields: {
      nl: 'requirements',
      en: 'requirementsEn',
      de: 'requirementsDe',
      fr: 'requirementsFr',
      pt: 'requirementsPt',
    },
  },
  {
    baseField: 'termsOfEmployment',
    languageFields: {
      nl: 'termsOfEmployment',
      en: 'termsOfEmploymentEn',
      de: 'termsOfEmploymentDe',
      fr: 'termsOfEmploymentFr',
      pt: 'termsOfEmploymentPt',
    },
  },
  {
    baseField: 'additionalInfo',
    languageFields: {
      nl: 'additionalInfo',
      en: 'additionalInfoEn',
      de: 'additionalInfoDe',
      fr: 'additionalInfoFr',
      pt: 'additionalInfoPt',
    },
  },
] as const;

/**
 * Helper to get field name for a specific language
 */
export function getLanguageFieldName(
  baseField: string,
  language: TranslationLanguage
): string {
  const config = COMPANY_PROFILE_FIELDS.find((f) => f.baseField === baseField);
  if (!config) {
    return baseField;
  }
  return config.languageFields[language];
}

/**
 * Helper to get language config by code
 */
export function getLanguageConfig(
  code: TranslationLanguage
): LanguageConfig | undefined {
  return TRANSLATION_LANGUAGES.find((l) => l.code === code);
}
